import React, { useState } from 'react';
import { Container, Layout, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { appTranslate } from '../../i18n';
import { SECTION_IDS } from '../../../consts';
import { useBI, useImpression, SECTION_NAMES } from '../../bi';
import { UnusualWayWizard } from '../UnusualWayWizard';
import { submitApplication } from '../UnusualWayWizard/submitApplication';
import { useTurnstile } from '../../lib/turnstile';
import { ASSETS } from '../../assets';
import { UnusualWayGlow } from './UnusualWayGlow';
import { glow } from './styles.ve.css';

export const UnusualWaySection = () => {
    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const bi = useBI();
    const impressionRef = useImpression<HTMLDivElement>(() =>
        bi.reportSectionImpression({ sectionName: SECTION_NAMES.unusualWay })
    );
    const { getToken } = useTurnstile();

    return (
        <Container id={SECTION_IDS.apply}>
            <Layout
                ref={impressionRef}
                position="relative"
                width="100%"
                style={{
                    backgroundColor: '#0c0c0d',
                    borderTopLeftRadius: '32px',
                    borderTopRightRadius: '32px',
                    overflow: 'clip',
                }}
            >
                {/* Desktop (md+): SVG glow with CSS rotation */}
                <Layout
                    hidden={{ default: true, md: false }}
                    className={glow}
                    position="absolute"
                    pointerEvents="none"
                    left={{ default: '49%', md: 'calc(50% - 206.56px)' }}
                    top={{ default: '100%', md: 'calc(50% + 245.61px)' }}
                    width={{ default: '1738px', md: '2632.56px' }}
                    height={{ default: '891px', md: '1350.25px' }}
                >
                    <UnusualWayGlow />
                </Layout>

                {/* Mobile (< md): rasterized image */}
                <Layout
                    hidden={{ default: false, md: true }}
                    position="absolute"
                    pointerEvents="none"
                    style={{ inset: 0 }}
                    width="100%"
                    height="100%"
                >
                    <img
                        src={ASSETS.unusualWayMobileBg}
                        alt=""
                        aria-hidden="true"
                        style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            pointerEvents: 'none',
                        }}
                    />
                </Layout>

                {/* Content */}
                <Stack
                    position="relative"
                    direction={{ default: 'column', md: 'row' }}
                    gap={{ default: '16', md: '32' }}
                    alignItems={{ default: 'center', md: 'flexEnd' }}
                    justifyContent="center"
                    paddingX={{ default: '5', md: '14' }}
                    paddingY={{ default: '10', md: '24' }}
                >
                    {/* Heading — mobile */}
                    <Typography
                        as="h2"
                        fontWeight="bold"
                        color="white"
                        hidden={{ default: false, md: true }}
                        letterSpacing="-0.96px"
                        style={{ fontSize: '64px', lineHeight: 1, textTransform: 'uppercase', textAlign: 'center' }}
                    >
                        {appTranslate('unusual_way.heading')}
                    </Typography>

                    {/* Heading — desktop */}
                    <Typography
                        as="h2"
                        fontWeight="semibold"
                        color="white"
                        hidden={{ default: true, md: false }}
                        letterSpacing="-5.4px"
                        style={{ fontSize: '90px', lineHeight: 1, textTransform: 'uppercase', width: '423px' }}
                    >
                        {appTranslate('unusual_way.heading')}
                    </Typography>

                    {/* Body + button column */}
                    <Stack
                        direction="column"
                        gap={{ default: '6', md: '10' }}
                        alignItems={{ default: 'center', md: 'flexStart' }}
                        maxWidth={{ md: '444px' }}
                    >
                        {/* Body — mobile */}
                        <Stack direction="column" gap="5" hidden={{ default: false, md: true }}>
                            <Typography
                                as="p"
                                fontWeight="light"
                                color="white"
                                letterSpacing="-0.8px"
                                style={{ fontSize: '20px', lineHeight: 1.4, textAlign: 'center' }}
                            >
                                {appTranslate('unusual_way.body_part_1')}
                            </Typography>
                            <Typography
                                as="p"
                                fontWeight="light"
                                color="white"
                                letterSpacing="-0.8px"
                                style={{ fontSize: '20px', lineHeight: 1.4, textAlign: 'center' }}
                            >
                                {appTranslate('unusual_way.body_part_2')}
                            </Typography>
                        </Stack>

                        {/* Body — desktop */}
                        <Stack direction="column" gap="5" hidden={{ default: true, md: false }} maxWidth="90%">
                            <Typography
                                as="p"
                                fontWeight="light"
                                color="white"
                                letterSpacing="-0.72px"
                                style={{ fontSize: '18px', lineHeight: 1.5 }}
                            >
                                {appTranslate('unusual_way.body_part_1')}
                            </Typography>
                            <Typography
                                as="p"
                                fontWeight="light"
                                color="white"
                                letterSpacing="-0.72px"
                                style={{ fontSize: '18px', lineHeight: 1.5 }}
                            >
                                {appTranslate('unusual_way.body_part_2')}
                            </Typography>
                        </Stack>

                        <Button
                            colorScheme="white"
                            size="lg"
                            fullWidth={{ default: true, md: false }}
                            style={{ borderRadius: 9999 }}
                            onClick={() => {
                                bi.reportCtaClick({ componentName: 'open_unusual_way_wizard' });
                                setIsWizardOpen(true);
                            }}
                        >
                            {appTranslate('unusual_way.apply_now')}
                        </Button>
                    </Stack>
                </Stack>
            </Layout>

            <UnusualWayWizard
                isOpen={isWizardOpen}
                onClose={() => setIsWizardOpen(false)}
                onSubmit={async (submission) => submitApplication(submission.answers, await getToken())}
            />
        </Container>
    );
};
